import { ApiService } from './api.service'

export interface PaymentPackage {
  id: string
  name: string
  description?: string
  stripePriceId: string
  amount: number
  currency: string
  billingInterval?: string
  enabledModules: string[]
  maxLicenses?: number
  isActive: boolean
}

export interface UpsertPaymentPackagePayload {
  name: string
  description?: string
  amount: number
  currency?: string
  billingInterval?: string
  enabledModules: string[]
  maxLicenses?: number
  isActive?: boolean
}

export interface CompanySubscription {
  stripeSubscriptionId: string
  stripePriceId: string
  packageId?: string
  status: string
  pendingStripePriceId?: string | null
  pendingPackageChangeAt?: string | null
  currentPeriodStart?: string
  currentPeriodEnd?: string
  cancelAtPeriodEnd: boolean
}

export interface CompanyInvoice {
  stripeInvoiceId: string
  companyId?: string
  companyName?: string
  amount: number
  currency: string
  status?: string
  billingReason?: string
  createdAt?: string
  hostedInvoiceUrl?: string
  invoicePdfUrl?: string
}

export interface CreateCheckoutSessionPayload {
  packageId: string
  companyId?: string
}

export interface ChangePackagePayload {
  targetPackageId: string
  companyId?: string
}

export interface CancelPendingPackageChangePayload {
  companyId?: string
}

export interface ChangePackageResponse {
  currentStripePriceId: string | null
  pendingStripePriceId?: string | null
  pendingPackageChangeAt?: string | null
  status: 'scheduled' | 'unchanged' | 'updated'
  message: string
}

export interface PackagesSyncSummary {
  fetchedStripePrices?: number
  createdPackages?: number
  updatedPackages?: number
  stripeSyncFailed?: boolean
  endpointUnavailable?: boolean
  synced?: number
  created?: number
  updated?: number
  fallbackCreated?: boolean
  usedCachedData?: boolean
  message?: string
  [key: string]: unknown
}

export class PaymentService extends ApiService {
  async listPackages(): Promise<PaymentPackage[]> {
    return this.handleResponse(this.api.get<PaymentPackage[]>('/payments/packages'))
  }

  async listPackagesForManagement(): Promise<PaymentPackage[]> {
    return this.handleResponse(this.api.get<PaymentPackage[]>('/payments/packages/manage'))
  }

  async createPackage(payload: UpsertPaymentPackagePayload): Promise<PaymentPackage> {
    return this.handleResponse(this.api.post<PaymentPackage>('/payments/packages', payload))
  }

  async updatePackage(
    id: string,
    payload: Partial<UpsertPaymentPackagePayload>
  ): Promise<PaymentPackage> {
    return this.handleResponse(this.api.patch<PaymentPackage>(`/payments/packages/${id}`, payload))
  }

  async createCheckoutSession(
    payload: CreateCheckoutSessionPayload
  ): Promise<{ url: string; sessionId: string }> {
    return this.handleResponse(
      this.api.post<{ url: string; sessionId: string }>('/payments/checkout-session', payload)
    )
  }

  async changePackage(payload: ChangePackagePayload): Promise<ChangePackageResponse> {
    return this.handleResponse(
      this.api.post<ChangePackageResponse>('/payments/change-package', payload)
    )
  }

  async cancelPendingPackageChange(
    payload?: CancelPendingPackageChangePayload
  ): Promise<ChangePackageResponse> {
    return this.handleResponse(
      this.api.post<ChangePackageResponse>('/payments/cancel-pending-package-change', payload || {})
    )
  }

  async getSubscription(companyId?: string): Promise<CompanySubscription | null> {
    try {
      return await this.handleResponse(
        this.api.get<CompanySubscription | null>('/payments/subscription', {
          params: companyId ? { companyId } : undefined,
        })
      )
    } catch (error: any) {
      if (error?.response?.status === 404) return null
      throw error
    }
  }

  async cancelSubscription(companyId?: string): Promise<CompanySubscription | null> {
    return this.handleResponse(
      this.api.post<CompanySubscription | null>(
        '/payments/subscription/cancel',
        companyId ? { companyId } : {}
      )
    )
  }

  async listInvoices(companyId?: string): Promise<CompanyInvoice[]> {
    return this.handleResponse(
      this.api.get<CompanyInvoice[]>('/payments/invoices', {
        params: companyId ? { companyId } : undefined,
      })
    )
  }

  async syncPackages(): Promise<PackagesSyncSummary> {
    try {
      return await this.handleResponse(
        this.api.post<PackagesSyncSummary>('/payments/packages/sync')
      )
    } catch (error: any) {
      // Backward compatibility for environments where sync endpoint is not yet deployed.
      if (error?.response?.status === 404) {
        await this.handleResponse(this.api.get<PaymentPackage[]>('/payments/packages/manage'))
        return {
          message:
            'Direkter Sync-Endpoint nicht verfuegbar. Pakete wurden ueber die Management-Liste neu geladen.',
          endpointUnavailable: true,
        }
      }

      throw error
    }
  }
}

export const paymentService = new PaymentService()
